import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  findQuestTreasureCollectionIndex,
  QUEST_TREASURE_COLLECTIONS,
} from "../constants/questTreasureCollections";
import { fetchOwnedRelicFileUrl, fetchQuestInventory } from "../utils/questApi";
import {
  readQuestTreasureCollectionIndex,
  readQuestTreasureCollectionMenuScroll,
  writeQuestTreasureCollectionIndex,
  writeQuestTreasureCollectionMenuScroll,
} from "../utils/questTreasureCollectionPreference";
import {
  readQuestTreasureSelection,
  writeQuestTreasureSelection,
} from "../utils/questTreasureSelectionPreference";
import {
  hasQuestTreasureScroll,
  readQuestTreasureScroll,
  writeQuestTreasureScroll,
} from "../utils/questTreasureScrollPreference";
import QuestRelicFbxViewer from "./QuestRelicFbxViewer";
import PackAssignDropdown from "./PackAssignDropdown";
import LockIcon from "./IconComponent/LockIcon";
import "./QuestTreasureHallView.css";
import "./QuestView.css";

const COLLECTION_MENU_VISIBLE_ROWS = 6;

function collectionMenuMaxHeight() {
  const raw =
    typeof document === "undefined"
      ? ""
      : getComputedStyle(document.documentElement).getPropertyValue("--text-scale");
  const textScale = parseFloat(raw);
  const scale = Number.isFinite(textScale) && textScale > 0 ? textScale : 1;
  const rowHeight = 16 + 1.35 * 16 * scale;
  return COLLECTION_MENU_VISIBLE_ROWS * rowHeight;
}

/**
 * Relique de bienvenue offerte à tout nouveau compte (voir API quêtes,
 * `WELCOME_RELIC_ID`) : première visite → sa collection est ouverte et elle
 * est sélectionnée, centrée dans la liste.
 */
const WELCOME_RELIC = { collectionId: "egyptian", relicId: "sistrum" };
const WELCOME_COLLECTION_INDEX = Math.max(
  0,
  findQuestTreasureCollectionIndex(WELCOME_RELIC.collectionId)
);

const ROMAN_NUMERALS = [
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
  "XI",
  "XII",
  "XIII",
  "XIV",
  "XV",
  "XVI",
  "XVII",
  "XVIII",
  "XIX",
  "XX",
  "XXI",
  "XXII",
  "XXIII",
  "XXIV",
];

export default function QuestTreasureHallView({ focus = null, onFocusApplied }) {
  const [collectionIndex, setCollectionIndex] = useState(() =>
    readQuestTreasureCollectionIndex(
      QUEST_TREASURE_COLLECTIONS.length,
      WELCOME_COLLECTION_INDEX
    )
  );
  const [selectionByCollection, setSelectionByCollection] = useState(() => {
    const initial = {};
    QUEST_TREASURE_COLLECTIONS.forEach((collection) => {
      const relicIds = collection.relics.map((relic) => relic.id);
      const fallbackId =
        collection.id === WELCOME_RELIC.collectionId &&
        relicIds.includes(WELCOME_RELIC.relicId)
          ? WELCOME_RELIC.relicId
          : collection.relics[0]?.id || null;
      initial[collection.id] = readQuestTreasureSelection(
        collection.id,
        relicIds,
        fallbackId
      );
    });
    return initial;
  });
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });
  const [ownedRelicIds, setOwnedRelicIds] = useState([]);
  const [customScrollbar, setCustomScrollbar] = useState({
    visible: false,
    top: 0,
    height: 0,
  });

  const listRef = useRef(null);
  const scrollerRef = useRef(null);
  const itemRefs = useRef(new Map());
  // Passe de restauration de scroll en cours : on saute l'auto-centrage sur
  // la relique sélectionnée pour ne pas écraser la position mémorisée.
  const pendingScrollRestoreRef = useRef(true);
  const collectionMenuScrollRef = useRef(readQuestTreasureCollectionMenuScroll());

  const collectionOptions = useMemo(
    () =>
      QUEST_TREASURE_COLLECTIONS.map((collection) => ({
        value: collection.id,
        label: collection.menuTitle,
      })).sort((left, right) =>
        left.label.localeCompare(right.label, "fr", { sensitivity: "base" })
      ),
    []
  );

  const activeCollection = QUEST_TREASURE_COLLECTIONS[collectionIndex];
  const collectionRelics = activeCollection.relics;

  const selectedRelicId = selectionByCollection[activeCollection.id] || null;

  const selectedRelic = useMemo(
    () => collectionRelics.find((relic) => relic.id === selectedRelicId) || null,
    [collectionRelics, selectedRelicId]
  );

  const ownedRelicSet = useMemo(
    () => new Set(ownedRelicIds),
    [ownedRelicIds]
  );
  const selectedRelicOwned = selectedRelic
    ? ownedRelicSet.has(selectedRelic.id)
    : false;
  const [relicModelUrl, setRelicModelUrl] = useState({ id: "", url: "" });

  useEffect(() => {
    if (!selectedRelic || !selectedRelicOwned) {
      setRelicModelUrl({ id: "", url: "" });
      return undefined;
    }

    const relicId = selectedRelic.id;
    const localUrl = selectedRelic.modelUrl;
    let cancelled = false;
    setRelicModelUrl({ id: relicId, url: "" });
    fetchOwnedRelicFileUrl(relicId).then((signedUrl) => {
      if (cancelled) return;
      setRelicModelUrl({ id: relicId, url: signedUrl || localUrl });
    });

    return () => {
      cancelled = true;
    };
  }, [selectedRelic, selectedRelicOwned]);
  const ownedInCollection = useMemo(
    () => collectionRelics.filter((relic) => ownedRelicSet.has(relic.id)).length,
    [collectionRelics, ownedRelicSet]
  );
  const discoveredRelics = useMemo(() => {
    const relics = QUEST_TREASURE_COLLECTIONS.flatMap((collection) => collection.relics);
    return {
      unlocked: relics.filter((relic) => ownedRelicSet.has(relic.id)).length,
      total: relics.length,
    };
  }, [ownedRelicSet]);

  useEffect(() => {
    let cancelled = false;

    const loadInventory = async () => {
      const inventory = await fetchQuestInventory();
      if (!cancelled) {
        setOwnedRelicIds(inventory?.ownedRelicIds || []);
      }
    };

    loadInventory();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadInventory();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    if (!focus?.collectionId || !focus?.relicId) return undefined;

    const nextCollectionIndex = findQuestTreasureCollectionIndex(focus.collectionId);
    if (nextCollectionIndex < 0) {
      onFocusApplied?.();
      return undefined;
    }

    const collection = QUEST_TREASURE_COLLECTIONS[nextCollectionIndex];
    const relicExists = collection.relics.some((relic) => relic.id === focus.relicId);
    if (!relicExists) {
      onFocusApplied?.();
      return undefined;
    }

    // Lien profond : on centre sur la relique ciblée plutôt que de restaurer
    // une position de scroll mémorisée.
    pendingScrollRestoreRef.current = false;
    setCollectionIndex(nextCollectionIndex);
    writeQuestTreasureCollectionIndex(nextCollectionIndex);
    setSelectionByCollection((current) => ({
      ...current,
      [focus.collectionId]: focus.relicId,
    }));
    writeQuestTreasureSelection(focus.collectionId, focus.relicId);

    let cancelled = false;
    fetchQuestInventory().then((inventory) => {
      if (!cancelled) {
        setOwnedRelicIds(inventory?.ownedRelicIds || []);
      }
    });

    onFocusApplied?.();

    return () => {
      cancelled = true;
    };
  }, [focus, onFocusApplied]);

  const goToCollection = useCallback((nextIndex) => {
    const count = QUEST_TREASURE_COLLECTIONS.length;
    const wrappedIndex = ((nextIndex % count) + count) % count;
    pendingScrollRestoreRef.current = true;
    setCollectionIndex(wrappedIndex);
    writeQuestTreasureCollectionIndex(wrappedIndex);
  }, []);

  const stepCollection = useCallback(
    (direction) => {
      const currentId = QUEST_TREASURE_COLLECTIONS[collectionIndex]?.id;
      const current = collectionOptions.findIndex((option) => option.value === currentId);
      const count = collectionOptions.length;
      if (!count) return;
      const start = current < 0 ? 0 : current;
      const next = collectionOptions[(start + direction + count) % count];
      const index = findQuestTreasureCollectionIndex(next.value);
      if (index >= 0) goToCollection(index);
    },
    [collectionIndex, collectionOptions, goToCollection]
  );

  const getCollectionMenuScroll = useCallback(
    () => collectionMenuScrollRef.current,
    []
  );

  const handleCollectionMenuScroll = useCallback((scrollTop) => {
    collectionMenuScrollRef.current = scrollTop;
    writeQuestTreasureCollectionMenuScroll(scrollTop);
  }, []);

  const handlePrevCollection = useCallback(() => {
    stepCollection(-1);
  }, [stepCollection]);

  const handleNextCollection = useCallback(() => {
    stepCollection(1);
  }, [stepCollection]);

  const updateScrollUi = useCallback(() => {
    const el = listRef.current;
    const scroller = scrollerRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;

    setScrollFades({
      top: scrollTop > threshold,
      bottom: scrollTop + clientHeight < scrollHeight - threshold,
    });

    const hasOverflow = scrollHeight > clientHeight + 1;
    if (!hasOverflow || !scroller) {
      setCustomScrollbar({ visible: false, top: 0, height: 0 });
      return;
    }

    const trackHeight = scroller.clientHeight;
    const thumbHeight = Math.max((clientHeight / scrollHeight) * trackHeight, 28);
    const maxThumbTop = Math.max(trackHeight - thumbHeight, 0);
    const scrollRatio = scrollTop / Math.max(scrollHeight - clientHeight, 1);
    const thumbTop = scrollRatio * maxThumbTop;

    setCustomScrollbar({
      visible: true,
      top: thumbTop,
      height: thumbHeight,
    });
  }, []);

  const handleScroll = useCallback(() => {
    updateScrollUi();
    const el = listRef.current;
    if (el && !pendingScrollRestoreRef.current) {
      writeQuestTreasureScroll(activeCollection.id, el.scrollTop);
    }
  }, [updateScrollUi, activeCollection.id]);

  const handleSelectRelic = useCallback(
    (relicId) => {
      setSelectionByCollection((current) => ({
        ...current,
        [activeCollection.id]: relicId,
      }));
      writeQuestTreasureSelection(activeCollection.id, relicId);
    },
    [activeCollection.id]
  );

  const navigateRelic = useCallback(
    (direction) => {
      if (!collectionRelics.length) return;

      const currentIndex = collectionRelics.findIndex(
        (relic) => relic.id === selectedRelicId
      );
      const nextIndex =
        currentIndex < 0
          ? direction === "down"
            ? 0
            : collectionRelics.length - 1
          : direction === "down"
            ? (currentIndex + 1) % collectionRelics.length
            : (currentIndex - 1 + collectionRelics.length) % collectionRelics.length;
      const next = collectionRelics[nextIndex];
      if (!next || next.id === selectedRelicId) return;

      handleSelectRelic(next.id);
    },
    [collectionRelics, selectedRelicId, handleSelectRelic]
  );

  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      const target = event.target;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "textarea" || target?.isContentEditable) return;
      if (tag === "input") {
        const type = String(target.type || "text").toLowerCase();
        if (type !== "radio" && type !== "checkbox" && type !== "button") return;
      }
      if (!collectionRelics.length) return;

      event.preventDefault();
      event.stopPropagation();
      navigateRelic(event.key === "ArrowDown" ? "down" : "up");
    };

    window.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown, true);
  }, [collectionRelics.length, navigateRelic]);

  useEffect(() => {
    updateScrollUi();
  }, [updateScrollUi, selectedRelicId, collectionIndex]);

  useEffect(() => {
    const el = listRef.current;
    const scroller = scrollerRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(updateScrollUi);
    observer.observe(el);
    if (scroller) observer.observe(scroller);
    return () => observer.disconnect();
  }, [updateScrollUi, collectionIndex]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    // Passe de restauration : on repose la position mémorisée pour la collection
    // active plutôt que de recentrer sur la relique sélectionnée.
    if (pendingScrollRestoreRef.current) {
      pendingScrollRestoreRef.current = false;
      if (hasQuestTreasureScroll(activeCollection.id)) {
        el.scrollTop = readQuestTreasureScroll(activeCollection.id);
      } else {
        // Première visite de cette collection : on centre la relique sélectionnée
        // (ex. le sistrum de bienvenue) pour qu'elle soit lisible d'emblée.
        const activeItem = itemRefs.current.get(selectedRelicId);
        if (activeItem) {
          const elRect = el.getBoundingClientRect();
          const itemRect = activeItem.getBoundingClientRect();
          // Rects en pixels écran (cadre éventuellement mis à l'échelle) → pixels de scroll.
          const ratio = elRect.height > 0 ? el.clientHeight / elRect.height : 1;
          el.scrollTop = Math.max(
            0,
            el.scrollTop +
              (itemRect.top - elRect.top - elRect.height / 2 + itemRect.height / 2) * ratio
          );
        } else {
          el.scrollTop = 0;
        }
      }
      updateScrollUi();
      return;
    }

    const activeItem = itemRefs.current.get(selectedRelicId);
    if (!activeItem) return;

    activeItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
    updateScrollUi();
  }, [selectedRelicId, collectionIndex, activeCollection.id, updateScrollUi]);

  return (
    <div className="quest-view quest-view--treasures">
      <header className="quest-view-header">
        <h2 className="quest-view-title">
          Salle des trésors de quête
          <span className="quest-treasure-discovered">
            ({discoveredRelics.unlocked}/{discoveredRelics.total} reliques découvertes)
          </span>
        </h2>
      </header>
      <div className="quest-view-body quest-view-body--treasures">
        <div className="quest-treasure-casket">
          <header className="quest-treasure-casket__head">
            <div className="quest-treasure-collection-nav">
              <button
                type="button"
                className="quest-treasure-catalog__arrow"
                onClick={handlePrevCollection}
                aria-label="Collection précédente"
              >
                <ChevronLeft size={16} strokeWidth={2.2} aria-hidden="true" />
              </button>

              <div className="quest-treasure-collection-nav__picker">
                <PackAssignDropdown
                  options={collectionOptions}
                  value={activeCollection.id}
                  onChange={(collectionId) => {
                    const index = findQuestTreasureCollectionIndex(collectionId);
                    if (index >= 0) goToCollection(index);
                  }}
                  ariaLabel="Choisir une collection de reliques"
                  matchTriggerWidth
                  openBelow
                  menuMaxHeight={collectionMenuMaxHeight()}
                  menuClassName="quest-treasure-collection-menu"
                  edgeFade
                  getMenuScrollTop={getCollectionMenuScroll}
                  onMenuScroll={handleCollectionMenuScroll}
                />
              </div>

              <button
                type="button"
                className="quest-treasure-catalog__arrow"
                onClick={handleNextCollection}
                aria-label="Collection suivante"
              >
                <ChevronRight size={16} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="quest-treasure-casket__content">
            <section
              className="quest-treasure-showcase"
              aria-label={
                selectedRelic
                  ? selectedRelicOwned
                    ? `Relique sélectionnée : ${selectedRelic.label}`
                    : `Relique verrouillée : ${selectedRelic.label}`
                  : "Aucune relique disponible"
              }
            >
              {selectedRelic && selectedRelicOwned ? (
                <QuestRelicFbxViewer
                  key={`${activeCollection.id}-${selectedRelic.id}`}
                  relicId={`${activeCollection.id}-${selectedRelic.id}`}
                  cameraFitOffset={selectedRelic.cameraFitOffset}
                  modelRotation={selectedRelic.modelRotation}
                  displayVariant="showcase"
                  showCaption={false}
                  lightingProfile={
                    activeCollection.id === "africanKingdom" ? "contrast" : "default"
                  }
                  modelUrl={
                    relicModelUrl.id === selectedRelic.id ? relicModelUrl.url : ""
                  }
                  label={selectedRelic.label}
                />
              ) : selectedRelic ? (
                <div className="quest-treasure-showcase__locked">
                  <div className="quest-treasure-showcase__locked-icon" aria-hidden="true">
                    <LockIcon size={54} color="currentColor" strokeWidth={1.6} />
                  </div>
                  <p className="quest-treasure-showcase__locked-title">{selectedRelic.label}</p>
                  <p className="quest-treasure-showcase__locked-message">
                    Cette relique n&apos;a pas encore été découverte. Accomplissez des quêtes pour
                    la débloquer.
                  </p>
                </div>
              ) : (
                <div className="quest-treasure-showcase__empty">
                  <p>Cette collection sera bientôt disponible.</p>
                </div>
              )}
            </section>

            <aside className="quest-treasure-catalog" aria-label="Catalogue des reliques">
              <header className="quest-treasure-catalog__head">
                <div className="quest-treasure-catalog__title-row">
                  <h3 className="quest-treasure-catalog__title">
                    {activeCollection.listTitle}
                    {collectionRelics.length > 0 ? (
                      <span
                        className="quest-treasure-catalog__count"
                        aria-label={`${ownedInCollection} reliques possédées sur ${collectionRelics.length}`}
                      >
                        {ownedInCollection}/{collectionRelics.length}
                      </span>
                    ) : null}
                  </h3>
                </div>
              </header>

              <div
                className={[
                  "quest-treasure-catalog__shell",
                  scrollFades.top && "quest-treasure-catalog__shell--fade-top",
                  scrollFades.bottom && "quest-treasure-catalog__shell--fade-bottom",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {collectionRelics.length === 0 ? (
                  <p className="quest-treasure-catalog__empty">
                    Les reliques de cette civilisation seront ajoutées prochainement.
                  </p>
                ) : (
                  <div ref={scrollerRef} className="quest-treasure-catalog__scroller">
                    <ul
                      ref={listRef}
                      className="quest-treasure-catalog__list"
                      onScroll={handleScroll}
                    >
                      {collectionRelics.map((relic, index) => {
                        const isActive = relic.id === selectedRelicId;
                        const isOwned = ownedRelicSet.has(relic.id);

                        return (
                          <li key={relic.id} className="quest-treasure-catalog__entry">
                            <button
                              type="button"
                              ref={(node) => {
                                if (node) itemRefs.current.set(relic.id, node);
                                else itemRefs.current.delete(relic.id);
                              }}
                              className={[
                                "quest-treasure-catalog__item",
                                isActive && "quest-treasure-catalog__item--active",
                                !isOwned && "quest-treasure-catalog__item--locked",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              aria-current={isActive ? "true" : undefined}
                              onClick={() => handleSelectRelic(relic.id)}
                            >
                              <span className="quest-treasure-catalog__index" aria-hidden="true">
                                {ROMAN_NUMERALS[index] || index + 1}
                              </span>
                              <span className="quest-treasure-catalog__label">{relic.label}</span>
                              <span
                                className="quest-treasure-catalog__lock"
                                aria-hidden={isOwned}
                                aria-label={
                                  isOwned ? undefined : "Relique verrouillée"
                                }
                              >
                                {!isOwned ? (
                                  <LockIcon
                                    size={15}
                                    color="currentColor"
                                    strokeWidth={1.9}
                                    aria-hidden="true"
                                  />
                                ) : null}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>

                    {customScrollbar.visible ? (
                      <div className="quest-treasure-catalog__scrollbar" aria-hidden="true">
                        <div
                          className="quest-treasure-catalog__scrollbar-thumb"
                          style={{
                            height: `${customScrollbar.height}px`,
                            transform: `translateY(${customScrollbar.top}px)`,
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}
